'use strict';

/**
 * Attempts to infer minimum and maximum allowed input length from a regex string.
 *
 * Notes:
 * - Expects common “anchored” patterns (leading `^` and trailing `$`) and strips them.
 * - Supports basic tokens/quantifiers such as character classes (`[...]`), `\\d`, `\\s`, `\\w`, `.`, `-`,
 *   and quantifiers `{n}`, `{n,m}`, `?`, `+`, `*`.
 * - If the pattern is unbounded (contains `+` or `*`), `max` is returned as `null`.
 *
 * @param {string} regexStr - Regex pattern as a string.
 * @returns {{min: (number|null), max: (number|null)}} Object containing `min` and `max` lengths; both are `null` when `regexStr` is empty.
 */
function getMinMaxLengthFromRegex(regexStr) {
    if (!regexStr) {
        return { min: null, max: null };
    }

    var pattern = regexStr.replace(/^\^|\$$/g, '');

    var min = 0;
    var max = 0;
    var unbounded = false;

    // Token + quantifier matcher
    var tokenRegex = /(\[[^\]]+\]|\\d|\\s|\\w|\.|-)(\{(\d+)(,(\d+))?\}|\+|\*|\?)?/g;

    var match = tokenRegex.exec(pattern);
    while (match !== null) {
        var quantifier = match[2];

        if (!quantifier) {
            min += 1;
            max += 1;
        } else if (quantifier.startsWith('{')) {
            var exact = match[3];
            var rangeMax = match[5];

            if (rangeMax) {
                min += parseInt(exact, 10);
                max += parseInt(rangeMax, 10);
            } else {
                var n = parseInt(exact, 10);
                min += n;
                max += n;
            }
        } else if (quantifier === '?') {
            // min += 0
            max += 1;
        } else if (quantifier === '+') {
            min += 1;
            unbounded = true;
        } else if (quantifier === '*') {
            unbounded = true;
        }

        match = tokenRegex.exec(pattern);
    }

    return {
        min: min,
        max: unbounded ? null : max
    };
}

/**
 * Builds a “disallowed characters” regex based on an allowlist inferred from the provided regex string.
 * The returned regex can be used to strip invalid characters via `value.replace(disallowedRegex, '')`.
 *
 * @param {string} regexStr - Regex pattern as a string.
 * @returns {RegExp|null} A global regex matching any character not in the inferred allowlist; `null` when `regexStr` is empty.
 */
function buildDisallowedRegex(regexStr) {
    if (!regexStr) return null;

    var allowDigits = /\\d|0-9/.test(regexStr);
    var allowLetters = /A-Za-z/.test(regexStr);
    var allowHyphen = /-/.test(regexStr);
    var allowSpace = /\\s/.test(regexStr);

    var allowed = '';
    if (allowDigits) allowed += '0-9';
    if (allowLetters) allowed += 'A-Za-z';
    if (allowHyphen) allowed += '\\-';
    if (allowSpace) allowed += '\\s';

    if (!allowed) allowed = '0-9';

    return new RegExp('[^' + allowed + ']', 'g');
}

/**
 * Validates a guest checkout field using contextual rules (name, email, phone, or generic required checks).
 * @param {JQuery} $field - The input/select element wrapped in jQuery whose value should be validated.
 * @returns {boolean} True when the field value complies with its rules; otherwise false with feedback applied.
 */
function validateField($field) {
    var value = $field.val();
    var isValid = true;
    var errorMsg = '';
    var id = $field.attr('id');

    // Detect field types dynamically
    var isFirstNameField = /^(guest|reg)?(Shipping|Billing)FirstName$/i.test(id);
    var isLastNameField = /^(guest|reg)?(Shipping|Billing)LastName$/i.test(id);
    var isNameField = isFirstNameField || isLastNameField;

    var isPhoneField = /^(guest|reg)?(Shipping|Billing)Phone$/i.test(id);
    var isPincodeField = /^(guest|reg)?(Shipping|Billing)(Pincode|Zip|PostalCode)$/i.test(id);
    var isEmailField = $field.attr('type') === 'email';
    var cleanedValue;
    var $selectedCountry;

    if (isNameField) {
        cleanedValue = value.replace(/[^A-Za-z\s]/g, '').replace(/^\s+/, '');
        if (cleanedValue !== value) $field.val(cleanedValue);

        value = cleanedValue;

        if (($field.prop('required') || $field.closest('.form-group').hasClass('required')) && !value.trim()) {
            isValid = false;
            errorMsg = $field.data('missing-error');
        }

        var pattern = /^[A-Za-z][A-Za-z\s]*$/;
        if (isValid && value && !pattern.test(value)) {
            isValid = false;
            errorMsg = $field.data('invalid-error');
        }
    } else if (isEmailField) {
        cleanedValue = value.replace(/[^A-Za-z0-9@._-]/g, '');
        if (cleanedValue !== value) $field.val(cleanedValue);

        value = cleanedValue.trim();

        if (($field.prop('required') || $field.closest('.form-group').hasClass('required')) && !value) {
            isValid = false;
            errorMsg = $field.data('missing-error');
        }

        var emailPattern = /^[A-Za-z0-9._-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
        if (isValid && value && !emailPattern.test(value)) {
            isValid = false;
            errorMsg = $field.data('pattern-mismatch');
        }
    } else if (isPhoneField) {
        // Get selected country
        $selectedCountry = $field
            .closest('.guest-checkout-address-form-container, #addressModal')
            .find('.ckc-country-drop-down-section .custom-option-value.selected');

        var countryCode = $selectedCountry.attr('data-phonedialcode')
            || $field.attr('data-countrycode');

        var requiredLength = parseInt(
            $selectedCountry.attr('data-phonemaxlength')
            || $selectedCountry.attr('data-phonelength'),
            10
        );

        $field.attr('maxlength', requiredLength);

        // Safety fallback
        if (!requiredLength || Number.isNaN(requiredLength)) {
            requiredLength = 10;
        }

        // Digits-only enforcement
        cleanedValue = value.replace(/[^0-9]/g, '');
        if (cleanedValue !== value) {
            $field.val(cleanedValue);
        }
        value = cleanedValue;

        // Autofill-safe length enforcement
        if (value.length > requiredLength) {
            value = value.substring(0, requiredLength); // keep first N digits
            $field.val(value);
        }

        // Required validation
        if (
            ($field.prop('required')
                || $field.closest('.form-group').hasClass('required'))
            && !value
        ) {
            isValid = false;
            errorMsg = $field.data('missing-error');
        }

        // India-specific rule (+91)
        if (isValid && countryCode === '+91' && value.length > 0) {
            if (!/^[6-9]/.test(value)) {
                $field.val('');
                value = '';
                isValid = false;
                errorMsg = $field.data('format-error');
            }
        }

        // Final length validation
        if (isValid && value.length !== requiredLength) {
            isValid = false;
            errorMsg = 'Enter valid ' + requiredLength + '-digit phone number';
        }
    } else if (isPincodeField) {
        $selectedCountry = $field
            .closest('.guest-checkout-address-form-container, #addressModal')
            .find('.ckc-country-drop-down-section .custom-option-value.selected');

        var pinRegexStr = $selectedCountry.attr('data-pincoderegex');
        var pinRegex = pinRegexStr ? new RegExp(pinRegexStr) : null;

        var disallowedRegex = buildDisallowedRegex(pinRegexStr);

        // Strip invalid characters
        if (disallowedRegex) {
            cleanedValue = value.replace(disallowedRegex, '');
            if (cleanedValue !== value) {
                $field.val(cleanedValue);
            }
            value = cleanedValue;
        }

        // Extract min / max length from regex
        var lengthInfo = getMinMaxLengthFromRegex(pinRegexStr);
        var minLength = lengthInfo.min;
        var maxLength = lengthInfo.max;

        // Block typing beyond max length
        if (maxLength && value.length > maxLength) {
            value = value.substring(0, maxLength);
            $field.val(value);
        }

        // Required validation
        if (
            ($field.prop('required')
                || $field.closest('.form-group').hasClass('required'))
            && !value
        ) {
            isValid = false;
            errorMsg = $field.data('missing-error');
        }

        // Length validation
        if (
            isValid
            && value
            && ((minLength && value.length < minLength)
                || (maxLength && value.length > maxLength))
        ) {
            isValid = false;
            errorMsg = $field.data('invalid-error') || 'Enter valid pincode';
        }

        // Final format validation
        if (isValid && value && pinRegex && !pinRegex.test(value)) {
            isValid = false;
            errorMsg = $field.data('invalid-error') || 'Enter valid pincode';
        }
    } else {
        value = value.trim();
        if (($field.prop('required') || $field.closest('.form-group').hasClass('required')) && !value) {
            isValid = false;
            errorMsg = $field.data('missing-error');
        }
    }

    // ------------------------------
    // Show/hide errors
    // ------------------------------
    var $errorDiv;
    var $wrapperDiv;

    if (isPhoneField) {
        $wrapperDiv = $field.closest('.ckc-phone-group');
        $errorDiv = $wrapperDiv.find('.invalid-feedback');

        if (!isValid) {
            $field.addClass('is-invalid');
            $wrapperDiv.addClass('phone-error');
            $errorDiv.text(errorMsg);
        } else {
            $field.removeClass('is-invalid');
            $wrapperDiv.removeClass('phone-error');
            $errorDiv.text('');
        }
    } else {
        $errorDiv = $field.siblings('.invalid-feedback');

        if (isFirstNameField) {
            var $nameWrapper = $field.closest('.name-title-wrapper');
            $errorDiv = $nameWrapper.find('.invalid-feedback');

            if (!isValid) {
                $nameWrapper.addClass('firstname-error');
            } else {
                $nameWrapper.removeClass('firstname-error');
            }
        }
        if (!isValid) {
            $field.addClass('is-invalid');
            $errorDiv.text(errorMsg);
        } else {
            $field.removeClass('is-invalid');
            $errorDiv.text('');
        }
    }

    return isValid;
}

/**
 * Populates city/state fields by reverse geocoding the supplied pincode for a shipping/billing block.
 * @param {string} idPrefix - Full field prefix (for example `guestShipping`) used to target related inputs.
 * @param {string} pincode - Postal code entered by the shopper.
 * @param {string} country - Country code tied to the postal lookup.
 */
function fetchPlace(idPrefix, pincode, country) {
    var googleApiKey = $('#google-maps-api-key').val();
    var apiUrl = 'https://maps.googleapis.com/maps/api/geocode/json?address=' + pincode + ',' + country + '&key=' + googleApiKey;

    $.getJSON(apiUrl, function (data) {
        $('#' + idPrefix + 'City').val('');
        $('#' + idPrefix + 'State').val('');

        if (data.status === 'OK' && data.results.length) {
            var result = data.results[0];
            var city = '';
            var state = '';

            result.address_components.forEach(function (component) {
                if (component.types.includes('locality')) {
                    city = component.long_name;
                }
                if (component.types.includes('administrative_area_level_1')) {
                    state = component.long_name;
                }
            });

            if (city) $('#' + idPrefix + 'City').val(city).trigger('input');
            if (state) $('#' + idPrefix + 'State').val(state).trigger('input');
        }
    });
}

module.exports = {
    validateField: validateField,
    fetchPlace: fetchPlace,
    getMinMaxLengthFromRegex: getMinMaxLengthFromRegex,
    buildDisallowedRegex: buildDisallowedRegex
};
